import { ApiOperationItems } from "~enums/apiOperationItems";
import { useApiOperations } from "~hooks/useApiOperations.ts";
import { useExportPersons } from "~pages/Persons/PersonListPage/hooks/useExportPersons.ts";
import { useImportPersons } from "~pages/Persons/PersonListPage/hooks/useImportPersons.ts";
import type { CreatePerson, UpdatePersonPayload } from "~pages/Persons/types/upsertPerson.ts";
import type { DefaultOperationSuccessResponse } from "~types/response.ts";
import { ensureDefaultOperationResponse, isDefaultOperationSucceeded } from "~utils/defaultOperationResponse.ts";
import { useCreatePerson } from "./useCreatePerson.ts";
import { useDeletePerson } from "./useDeletePerson.ts";
import { useUpdatePerson } from "./useUpdatePerson.ts";

export const usePersonService = () => {
  const { mutateAsync: createPersonFn, isPending: isCreatePending } = useCreatePerson();
  const { mutateAsync: updatePersonFn, isPending: isUpdatePending } = useUpdatePerson();
  const { mutateAsync: deletePersonFn, isPending: isDeletePending } = useDeletePerson();
  const { mutateAsync: importPersonsFn, isPending: isImportPending } = useImportPersons();
  const { mutateAsync: exportPersonsFn, isPending: isExportPending } = useExportPersons();

  const { create, update, remove, execute } = useApiOperations(ApiOperationItems.PERSON);

  const createPerson = (payload: CreatePerson) =>
    create(() => createPersonFn(payload), {
      successMessageId: "person.add.success",
    });

  const updatePerson = (personId: string, payload: UpdatePersonPayload) =>
    update(() => updatePersonFn({ ...payload, personId }), {
      successMessageId: "person.update.success",
    });

  const deletePerson = async (personId: string) =>
    Boolean(await remove(() => deletePersonFn(personId), { successMessageId: "person.delete.success" }));

  const importPersons = async (fileName: string) =>
    ensureDefaultOperationResponse(
      await execute(() => importPersonsFn(fileName), {
        successMessageId: "persons.import.started",
        isSucceeded: isDefaultOperationSucceeded,
      }),
      "Persons import was not started",
    );

  const exportPersons = async (fieldNames: Array<string>): Promise<DefaultOperationSuccessResponse> =>
    ensureDefaultOperationResponse(
      await execute(() => exportPersonsFn(fieldNames), {
        successMessageId: "persons.export.started",
        isSucceeded: isDefaultOperationSucceeded,
      }),
      "Persons export was not started",
    );

  return {
    createPerson,
    isCreatePending,
    updatePerson,
    isUpdatePending,
    deletePerson,
    isDeletePending,
    importPersons,
    isImportPending,
    exportPersons,
    isExportPending,
  };
};
